import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useRealtimeChannel, threadChannel } from '@/core/realtime';
import { appendMessage, threadKeys } from './queries';
import type { MessagePublication } from './types';

/**
 * Bind one chat thread's `thread:` channel to its message cache (FE-ADR-017).
 *
 * Incoming messages are appended through `appendMessage`, which dedupes by
 * `public_id` — so the poster's own REST copy and the socket copy of the same
 * message render once.
 *
 * The thread LIST is invalidated rather than patched: its ordering key
 * (`last_message_at`) and its per-thread `unread_count` are both computed
 * server-side (backend ADR-020), and recomputing them in the client would be a
 * second implementation of rules that already exist — and a wrong one the
 * moment the read cursor moves on another device.
 */
export function useThreadChannel(threadPublicId: string | null | undefined): void {
  const qc = useQueryClient();

  const onPublication = useCallback(
    (payload: MessagePublication) => {
      if (payload?.type !== 'message.created' || !payload.message) return;
      appendMessage(qc, payload.thread, payload.message);
      void qc.invalidateQueries({ queryKey: threadKeys.lists() });
    },
    [qc]
  );

  useRealtimeChannel<MessagePublication>(
    threadPublicId ? threadChannel(threadPublicId) : null,
    onPublication
  );
}
